import { useEffect, useMemo, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import toast from 'react-hot-toast';
import { exportCandidates, getCandidateFilters, getCandidates } from '@/api/candidates';
import type {
  Candidate,
  CandidateFilterOptions,
  CandidateQuery,
  SortOrder,
} from '@/types/candidate';
import { useDebounce } from '@/hooks/useDebounce';
import StoreAutocomplete from './StoreAutocomplete';

const PAGE_SIZE = 20;
const INITIAL_QUERY: CandidateQuery = {
  page: 1,
  limit: PAGE_SIZE,
  dateFrom: '',
  dateTo: '',
  fullName: '',
  storeIds: [],
  statuses: [],
  sortBy: 'receivedAt',
  sortOrder: 'desc',
};

const columns: Array<{ key: keyof Candidate; label: string; className?: string }> = [
  { key: 'fullName', label: 'ФИО', className: 'min-w-[240px]' },
  { key: 'phone', label: 'Телефон', className: 'min-w-[150px]' },
  { key: 'email', label: 'E-mail', className: 'min-w-[230px]' },
  { key: 'receivedAt', label: 'Дата поступления', className: 'min-w-[175px]' },
  { key: 'vacancyName', label: 'Вакансия', className: 'min-w-[230px]' },
  { key: 'storeName', label: 'Магазин', className: 'min-w-[260px]' },
  { key: 'status', label: 'Последний статус', className: 'min-w-[180px]' },
  { key: 'statusUpdatedAt', label: 'Дата статуса', className: 'min-w-[175px]' },
];

const formatDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    : '—';

const SortIcon = ({ active, order }: { active: boolean; order: SortOrder }) => (
  <span className={`ml-1 text-xs ${active ? 'text-[#631d76]' : 'text-gray-300'}`}>
    {active && order === 'desc' ? '▼' : '▲'}
  </span>
);

const CandidatesPage = () => {
  const { keycloak } = useKeycloak();
  const [query, setQuery] = useState<CandidateQuery>(INITIAL_QUERY);
  const [nameInput, setNameInput] = useState('');
  const debouncedName = useDebounce(nameInput, 350);
  const [rows, setRows] = useState<Candidate[]>([]);
  const [count, setCount] = useState(0);
  const [options, setOptions] = useState<CandidateFilterOptions>({ stores: [], statuses: [] });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const allVisibleSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));

  useEffect(() => {
    setQuery((current) => {
      if (current.fullName === debouncedName) return current;
      return { ...current, fullName: debouncedName, page: 1 };
    });
  }, [debouncedName]);

  useEffect(() => {
    if (!keycloak.token) return;
    getCandidateFilters(keycloak.token)
      .then(setOptions)
      .catch((error) => toast.error(error.message));
  }, [keycloak.token]);

  useEffect(() => {
    if (!keycloak.token) return;
    let active = true;
    setLoading(true);
    getCandidates(query, keycloak.token)
      .then((result) => {
        if (!active) return;
        setRows(result.rows);
        setCount(result.count);
        setSelected(new Set());
      })
      .catch((error) => active && toast.error(error.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [keycloak.token, query]);

  const rangeLabel = useMemo(() => {
    if (!count) return '0 записей';
    const from = (query.page - 1) * PAGE_SIZE + 1;
    return `${from}–${Math.min(query.page * PAGE_SIZE, count)} из ${count}`;
  }, [count, query.page]);

  const updateQuery = <K extends keyof CandidateQuery>(key: K, value: CandidateQuery[K]) => {
    setQuery((current) => ({ ...current, [key]: value, page: 1 }));
  };

  const changeSort = (key: keyof Candidate) => {
    setQuery((current) => ({
      ...current,
      page: 1,
      sortBy: key,
      sortOrder: current.sortBy === key && current.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleAll = () =>
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) rows.forEach((row) => next.delete(row.id));
      else rows.forEach((row) => next.add(row.id));
      return next;
    });

  const toggleRow = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const resetFilters = () => {
    setNameInput('');
    setQuery(INITIAL_QUERY);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCandidates(query, keycloak.token);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ошибка выгрузки');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-[1800px] flex-col text-gray-800">
      <section className="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 xl:grid-cols-[1fr_1fr_1.2fr_1.5fr_1.3fr_auto]">
          <label className="text-sm font-medium text-gray-600">
            Дата с
            <input
              type="date"
              value={query.dateFrom}
              onChange={(event) => updateQuery('dateFrom', event.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-[#631d76]"
            />
          </label>
          <label className="text-sm font-medium text-gray-600">
            Дата по
            <input
              type="date"
              value={query.dateTo}
              onChange={(event) => updateQuery('dateTo', event.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-[#631d76]"
            />
          </label>
          <label className="text-sm font-medium text-gray-600">
            ФИО
            <input
              type="search"
              placeholder="Начните вводить ФИО"
              value={nameInput}
              onChange={(event) => setNameInput(event.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 outline-none focus:border-[#631d76]"
            />
          </label>
          <div className="text-sm font-medium text-gray-600">
            <label htmlFor="candidate-store-search">Магазины</label>
            <StoreAutocomplete
              options={options.stores}
              value={query.storeIds}
              onChange={(storeIds) => updateQuery('storeIds', storeIds)}
            />
          </div>
          <label className="text-sm font-medium text-gray-600">
            Статусы
            <div className="relative mt-1">
              <select
                value={query.statuses[0] ?? ''}
                onChange={(event) =>
                  updateQuery('statuses', event.target.value ? [event.target.value] : [])
                }
                className="block h-[42px] w-full appearance-none rounded border border-gray-300 bg-white px-3 pr-9 outline-none focus:border-[#631d76]"
              >
                <option value="">Все статусы</option>
                {options.statuses.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400"
                aria-hidden="true"
              >
                ▼
              </span>
            </div>
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={resetFilters}
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium transition hover:bg-gray-100"
            >
              Сбросить
            </button>
            <button
              type="button"
              disabled={exporting}
              onClick={handleExport}
              className="whitespace-nowrap rounded bg-[#631d76] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#674d81] disabled:opacity-50"
            >
              {exporting ? 'Формирование…' : 'Выгрузить Excel'}
            </button>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-500">
          В фильтре магазинов доступен поиск. Выбрано строк: {selected.size}.
        </div>
      </section>

      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="sticky top-0 z-10 bg-gray-100 text-gray-800">
              <tr>
                <th className="w-12 border-b border-gray-300 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    aria-label="Выбрать страницу"
                  />
                </th>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`border-b border-gray-300 px-4 py-3 ${column.className ?? ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => changeSort(column.key)}
                      className="flex items-center font-bold hover:text-[#631d76]"
                    >
                      {column.label}
                      <SortIcon active={query.sortBy === column.key} order={query.sortOrder} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="h-56 text-center text-gray-500">
                    Загрузка кандидатов…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="h-56 text-center text-gray-500">
                    По выбранным фильтрам кандидаты не найдены
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-b border-gray-200 font-medium text-gray-600 hover:bg-gray-100 ${selected.has(row.id) ? 'bg-purple-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                        aria-label={`Выбрать ${row.fullName}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{row.fullName || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{row.phone || '—'}</td>
                    <td className="px-4 py-3">{row.email || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(row.receivedAt)}</td>
                    <td className="px-4 py-3">{row.vacancyName || '—'}</td>
                    <td className="px-4 py-3">{row.storeName || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="rounded bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-800">
                        {row.statusName || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatDate(row.statusUpdatedAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 text-sm">
          <span className="text-gray-500">{rangeLabel}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={query.page <= 1 || loading}
              onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}
              className="rounded border border-gray-300 px-3 py-1.5 transition hover:bg-gray-100 disabled:opacity-40"
            >
              Назад
            </button>
            <span className="px-2">
              Страница {query.page} из {pages}
            </span>
            <button
              type="button"
              disabled={query.page >= pages || loading}
              onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}
              className="rounded border border-gray-300 px-3 py-1.5 transition hover:bg-gray-100 disabled:opacity-40"
            >
              Вперёд
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CandidatesPage;
